/**
 * SmartLink A/B rotation — data-layer client.
 *
 * Thin typed wrapper around `apiClient` for the
 * `/api/v1/smartlink-ab` surface (feature #111). Mirrors the contract
 * declared in `apps/api/src/routes/smartlink-ab.ts`:
 *
 *   PUT  /:smartlink_id/variants  body: [{variant_key, offer_id, split_bps}, ...]
 *   GET  /:smartlink_id           list variants
 *   POST /:smartlink_id/assign    body: {click_id} -> picked variant
 *
 * The sum of `split_bps` across rows MUST equal exactly 10_000 before a
 * save is attempted — the API will 400 otherwise. This module ships a
 * hand-rolled `safeParse` style validator so the form can surface the
 * failure before it hits the network.
 *
 * Keep this file dependency-free beyond the root `apiClient` — no React,
 * no TanStack — so it can be reused from server-side smoke harnesses.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Constants & regex mirrors of the API validators.
// ---------------------------------------------------------------------------

/** Total basis points across all active variants. API will reject any sum != this. */
export const TOTAL_BPS = 10_000;
/** Hard ceiling enforced by the API. */
export const MAX_VARIANTS = 32;
/** Upper length cap on any click_id the tester input accepts. */
export const CLICK_ID_MAX = 80;

const SMARTLINK_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const OFFER_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const VARIANT_KEY_RE = /^[A-Za-z0-9_-]{1,40}$/;
const CLICK_ID_RE = /^[A-Za-z0-9_-]{1,80}$/;

// ---------------------------------------------------------------------------
// Row + payload types.
// ---------------------------------------------------------------------------

export interface VariantDraft {
	variant_key: string;
	offer_id: string;
	split_bps: number;
}

export interface VariantRow extends VariantDraft {
	id: string;
	tenant_id: string;
	smartlink_id: string;
	active: boolean;
	created_at: number;
	updated_at: number;
}

export interface ListVariantsResponse {
	smartlink_id: string;
	variants: VariantRow[];
}

export interface SaveVariantsResponse {
	smartlink_id: string;
	variants: VariantRow[];
}

export interface AssignResponse {
	smartlink_id: string;
	click_id: string;
	variant_key: string;
	offer_id: string;
	ts: number;
}

// ---------------------------------------------------------------------------
// Validation — hand-rolled `safeParse` surface. Mirrors the API's
// `validateVariants` so the form reports the same rule set locally.
// ---------------------------------------------------------------------------

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	/** Keyed by row index (`row_0_invalid_split_bps`) or `_global` for totals. */
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export interface VariantsFormInput {
	smartlinkId: string;
	rows: VariantDraft[];
}

export interface VariantsFormParsed {
	smartlinkId: string;
	rows: VariantDraft[];
}

export function sumBps(rows: readonly VariantDraft[]): number {
	let n = 0;
	for (const r of rows) n += Number.isFinite(r.split_bps) ? r.split_bps : 0;
	return n;
}

export function isValidSmartlinkId(v: string): boolean {
	return SMARTLINK_ID_RE.test(v);
}

export const variantsFormSchema = {
	safeParse(input: VariantsFormInput): ParseResult<VariantsFormParsed> {
		const errors: Record<string, string> = {};

		const smartlinkId = (input.smartlinkId ?? "").trim();
		if (smartlinkId.length === 0) {
			errors.smartlinkId = "SmartLink ID is required.";
		} else if (!SMARTLINK_ID_RE.test(smartlinkId)) {
			errors.smartlinkId =
				"Use only letters, digits, '-' or '_' (1-64 chars).";
		}

		const rows = Array.isArray(input.rows) ? input.rows : [];
		if (rows.length === 0) {
			errors._global = "Add at least one variant.";
		} else if (rows.length > MAX_VARIANTS) {
			errors._global = `Maximum ${MAX_VARIANTS} variants.`;
		}

		const seen = new Set<string>();
		const cleaned: VariantDraft[] = [];
		for (let i = 0; i < rows.length; i += 1) {
			const r = rows[i]!;
			const key = (r.variant_key ?? "").trim();
			const offer = (r.offer_id ?? "").trim();
			const bps = r.split_bps;

			if (!VARIANT_KEY_RE.test(key)) {
				errors[`row_${i}_variant_key`] =
					"Use 1-40 letters, digits, '-' or '_'.";
			}
			if (!OFFER_ID_RE.test(offer)) {
				errors[`row_${i}_offer_id`] =
					"Use 1-64 letters, digits, '-' or '_'.";
			}
			if (
				typeof bps !== "number" ||
				!Number.isFinite(bps) ||
				!Number.isInteger(bps) ||
				bps < 0 ||
				bps > TOTAL_BPS
			) {
				errors[`row_${i}_split_bps`] = `Split bps must be an integer 0-${TOTAL_BPS}.`;
			}
			if (key.length > 0) {
				if (seen.has(key)) {
					errors[`row_${i}_variant_key`] = "Duplicate variant key.";
				} else {
					seen.add(key);
				}
			}
			cleaned.push({ variant_key: key, offer_id: offer, split_bps: bps });
		}

		if (!errors._global && rows.length > 0) {
			const total = sumBps(cleaned);
			if (total !== TOTAL_BPS) {
				errors._global = `Split must total exactly ${TOTAL_BPS} bps (current: ${total}).`;
			}
		}

		if (Object.keys(errors).length > 0) {
			return { success: false, errors };
		}
		return {
			success: true,
			data: { smartlinkId, rows: cleaned },
		};
	},
};

export function validateClickId(raw: string): { ok: true; value: string } | { ok: false; error: string } {
	const v = raw.trim();
	if (v.length === 0) {
		return { ok: false, error: "Click ID is required." };
	}
	if (!CLICK_ID_RE.test(v)) {
		return {
			ok: false,
			error: "Use 1-80 letters, digits, '-' or '_'.",
		};
	}
	return { ok: true, value: v };
}

// ---------------------------------------------------------------------------
// Remote operations — consumers wire these via TanStack Query.
// ---------------------------------------------------------------------------

const BASE = "/smartlink-ab";

/**
 * Returns the variants currently bound to a SmartLink, or an empty list if
 * none are configured yet. 404 from the API is normalized to `[]` so the
 * page can render the empty editor state without an error toast.
 */
export async function listVariants(smartlinkId: string): Promise<VariantRow[]> {
	try {
		const res = await apiClient.get<ListVariantsResponse>(
			`${BASE}/${encodeURIComponent(smartlinkId)}`,
		);
		return Array.isArray(res.variants) ? res.variants : [];
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return [];
		throw err;
	}
}

export interface SaveVariantsArgs {
	smartlinkId: string;
	rows: VariantDraft[];
}

/**
 * Replace the full variant set for a SmartLink. Atomic on the API side —
 * either every row writes or none do. The sum of `split_bps` MUST equal
 * `TOTAL_BPS`; the caller should have already validated.
 */
export async function saveVariants(
	args: SaveVariantsArgs,
): Promise<VariantRow[]> {
	const res = await apiClient.put<SaveVariantsResponse>(
		`${BASE}/${encodeURIComponent(args.smartlinkId)}/variants`,
		args.rows.map((r) => ({
			variant_key: r.variant_key,
			offer_id: r.offer_id,
			split_bps: r.split_bps,
		})),
	);
	return Array.isArray(res.variants) ? res.variants : [];
}

export interface AssignArgs {
	smartlinkId: string;
	clickId: string;
}

/**
 * Send a test click through the deterministic bucketer and return the
 * assigned variant. Writes an impression row server-side.
 */
export async function assignClick(args: AssignArgs): Promise<AssignResponse> {
	const res = await apiClient.post<AssignResponse>(
		`${BASE}/${encodeURIComponent(args.smartlinkId)}/assign`,
		{ click_id: args.clickId },
	);
	return res;
}

export const smartlinkAbQueryKeys = {
	all: ["smartlink-ab"] as const,
	bySmartlink: (smartlinkId: string) =>
		[...smartlinkAbQueryKeys.all, smartlinkId] as const,
};
